"use client"

import type React from "react"

import { useState } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Plus, Trash2, Edit, Mail, MessageSquare, Eye, Search, ChevronDown } from "lucide-react"
//import { mockMessageTemplates } from "@/lib/mock-org-data"
import type { MessageTemplate } from "@/types"
import OrgLayout from "@/layouts/OrgLayout"

export interface MessageTemplateProps {
  message_templates: MessageTemplate[]
}

function MessageTemplates({message_templates}: MessageTemplateProps) {
  const [templates, setTemplates] = useState<MessageTemplate[]>(message_templates)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [editing, setEditing] = useState<MessageTemplate | null>(null)
  const [previewing, setPreviewing] = useState<MessageTemplate | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [openTemplates, setOpenTemplates] = useState<Set<number>>(new Set())

  const toggleTemplate = (id: number) => {
    const newOpen = new Set(openTemplates)
    if (newOpen.has(id)) {
      newOpen.delete(id)
    } else {
      newOpen.add(id)
    }
    setOpenTemplates(newOpen)
  }

  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const name = formData.get("name") as string
    const template_type = formData.get("type") as "email" | "sms"
    const subject = formData.get("subject") as string
    const body = formData.get("body") as string
    const variablesStr = formData.get("variables") as string
    const variables = variablesStr
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean)

    if (editing) {
      setTemplates(
        templates.map((t) =>
          t.id === editing.id
            ? {
                ...t,
                name,
                template_type,
                subject: template_type === "email" ? subject : undefined,
                body,
                variables,
                updated_at: new Date().toISOString(),
              }
            : t,
        ),
      )
    } else {
      const newTemplate: Partial<MessageTemplate> = {
        name,
        template_type,
        subject: template_type === "email" ? subject : undefined,
        body,
        variables,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }
      console.log("New Template:", newTemplate)
    }
    setDialogOpen(false)
    setEditing(null)
  }

  const handleDelete = (id: number) => {
    setTemplates(templates.filter((t) => t.id !== id))
  }

  const filteredTemplates = templates.filter(
    (t) =>
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.template_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.body.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.subject?.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  return (
    <div className="space-y-6 w-full">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Message Templates</h2>
          <p className="text-sm text-muted-foreground">Manage email and SMS templates for automated actions</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setEditing(null)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Template
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <form onSubmit={handleSave}>
              <DialogHeader>
                <DialogTitle>{editing ? "Edit Template" : "Add Template"}</DialogTitle>
                <DialogDescription>
                  {editing ? "Update message template" : "Create a new message template"}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Template Name</Label>
                  <Input
                    id="name"
                    name="name"
                    defaultValue={editing?.name}
                    placeholder="Urgent Referral Notification"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">Type</Label>
                  <Select name="type" defaultValue={editing?.template_type || "email"}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject (Email only)</Label>
                  <Input
                    id="subject"
                    name="subject"
                    defaultValue={editing?.subject}
                    placeholder="Urgent Referral - {{patient_name}}"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="body">Message Body</Label>
                  <Textarea
                    id="body"
                    name="body"
                    defaultValue={editing?.body}
                    placeholder="Use {{variable_name}} for dynamic content"
                    rows={6}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Reference variables with {"{{"} variable_name {"}}"}. Variable names come from the JSON fields you
                    extracted in your document classification.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="variables">Variables (comma-separated)</Label>
                  <Input
                    id="variables"
                    name="variables"
                    defaultValue={editing?.variables.join(", ")}
                    placeholder="patient_name, referring_provider, reason"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    These variables can be used in the template with {"{{"} and {"}}"}
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button type="submit">{editing ? "Update" : "Create"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-2">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search templates by name, type, or content..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Template Preview</DialogTitle>
            <DialogDescription>Preview how your template will look</DialogDescription>
          </DialogHeader>
          {previewing && (
            <div className="space-y-4 py-4">
              {previewing.subject && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Subject</Label>
                  <div className="p-3 border rounded-md bg-muted/30">
                    <p className="text-sm">{previewing.subject}</p>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Body</Label>
                <div className="p-4 border rounded-md bg-muted/30">
                  <p className="text-sm whitespace-pre-wrap">{previewing.body}</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Available Variables</Label>
                <div className="flex flex-wrap gap-2">
                  {previewing.variables.map((variable, idx) => (
                    <span key={idx} className="px-2 py-1 text-xs bg-secondary text-secondary-foreground rounded-md">
                      {"{{"} {variable} {"}}"}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="grid gap-4">
        {filteredTemplates.map((template) => (
          <Collapsible
            key={template.id}
            open={openTemplates.has(template.id)}
            onOpenChange={() => toggleTemplate(template.id)}
          >
            <Card className="p-6">
              <CollapsibleTrigger className="w-full">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                      {template.template_type === "email" ? (
                        <Mail className="h-5 w-5 text-primary" />
                      ) : (
                        <MessageSquare className="h-5 w-5 text-primary" />
                      )}
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold">{template.name}</h3>
                      <p className="text-sm text-muted-foreground capitalize">{template.template_type}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${openTemplates.has(template.id) ? "rotate-180" : ""}`}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation()
                        setPreviewing(template)
                        setPreviewOpen(true)
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation()
                        setEditing(template)
                        setDialogOpen(true)
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDelete(template.id)
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <div className="space-y-3">
                  {template.subject && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Subject</Label>
                      <p className="text-sm">{template.subject}</p>
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Body</Label>
                    <p className="text-sm whitespace-pre-wrap line-clamp-3">{template.body}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Variables</Label>
                    <div className="flex flex-wrap gap-2">
                      {template.variables?.map((variable, idx) => (
                        <span key={idx} className="px-2 py-1 text-xs bg-secondary text-secondary-foreground rounded-md">
                          {"{{"} {variable} {"}}"}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">You have no templates or No templates found matching your search.</p>
        </Card>
      )}
    </div>
  )
}
MessageTemplates.layout = (page: React.ReactNode) => <OrgLayout activeSection="message_templates">{page}</OrgLayout>
export default MessageTemplates
